import {
    DashboardOutlined,
    DisconnectOutlined,
    LinkOutlined,
    LoginOutlined,
    ReloadOutlined,
    UserSwitchOutlined,
} from "@ant-design/icons";
import { Dropdown, MenuProps, Spin } from "antd";
import { UserRole } from "tm-shared/types";

import { useAppSelector, useOfflineModeSelector } from "@/hooks/redux";
import { PrimaryText, SecondaryText } from "@/shared/ui/Typography";

import { SIZES } from "../../constants";
import { StyledActionButton, StyledMoreOutlined } from "../../Styled";

interface MemberActionsProps {
    isProcessing: boolean;
    hasSchedule: boolean;
    canAccess: boolean;
    onLoad: () => void;
    onNavigate: () => void;
    onDashboard: () => void;
    onDelete: () => void;
    onCreateCustomTask: () => void;
}

export const MemberActions = ({
    isProcessing,
    hasSchedule,
    canAccess,
    onLoad,
    onNavigate,
    onDashboard,
    onDelete,
    onCreateCustomTask,
}: MemberActionsProps) => {
    const role = useAppSelector((state) => state.user.role);
    const offlineMode = useOfflineModeSelector();
    const isSVM = role === UserRole.Svm;

    const stop = (e?: React.MouseEvent) => e?.stopPropagation();

    const dropdownItems: NonNullable<MenuProps["items"]> = [
        {
            key: "dashboard",
            icon: <DashboardOutlined style={{ fontSize: 18 }} />,
            label: <SecondaryText>Дашборд</SecondaryText>,
            disabled: isSVM || offlineMode,
        },
        {
            key: "load",
            icon: hasSchedule ? (
                <ReloadOutlined style={{ fontSize: 18 }} />
            ) : (
                <LinkOutlined style={{ fontSize: 18 }} />
            ),
            label: (
                <SecondaryText>
                    {hasSchedule
                        ? "Перезавантажити маршрут"
                        : "Підключити маршрут"}
                </SecondaryText>
            ),
            disabled: isProcessing || offlineMode,
        },
        {
            key: "customTask",
            icon: <UserSwitchOutlined style={{ fontSize: 18 }} />,
            label: <SecondaryText>Створити доручення</SecondaryText>,
            disabled: false,
        },
        ...(canAccess
            ? [
                  {
                      key: "navigate",
                      icon: <LoginOutlined style={{ fontSize: 18 }} />,
                      label: (
                          <SecondaryText>Відкрити план роботи</SecondaryText>
                      ),
                      disabled: !canAccess,
                  },
              ]
            : []),
        ...(hasSchedule ? [{ type: "divider" as const, key: "divider1" }] : []),
        ...(hasSchedule
            ? [
                  {
                      key: "delete",
                      icon: (
                          <DisconnectOutlined
                              style={{ fontSize: 18, color: "#BF2E2D" }}
                          />
                      ),
                      label: (
                          <PrimaryText textColor="error">
                              Відключити маршрут
                          </PrimaryText>
                      ),
                      danger: true,
                      disabled: !hasSchedule,
                  },
              ]
            : []),
    ];

    const handleMenuClick: MenuProps["onClick"] = ({ key, domEvent }) => {
        domEvent.stopPropagation();

        switch (key) {
            case "dashboard":
                onDashboard();
                break;
            case "load":
                onLoad();
                break;
            case "navigate":
                onNavigate();
                break;
            case "delete":
                onDelete();
                break;
            case "customTask":
                onCreateCustomTask();
                break;
        }
    };

    if (isProcessing) {
        return (
            <StyledActionButton
                size={SIZES.ACTION_BUTTON}
                icon={<Spin size="small" />}
                disabled
                onClick={stop}
            />
        );
    }

    return (
        <Dropdown
            trigger={["click"]}
            menu={{
                items: dropdownItems,
                onClick: handleMenuClick,
            }}
        >
            <span onClick={stop}>
                <StyledMoreOutlined />
            </span>
        </Dropdown>
    );
};
